const STORAGE_KEY = 'currentUser';

// Relying-party identity for WebAuthn. Logic:
//   1. localhost → 'localhost' (dev).
//   2. Any *.netlify.app hostname (prod cpx-prototype.netlify.app, deploy previews,
//      branch deploys) → use the hostname directly. Each Netlify URL gets its own
//      RP scope; a credential enrolled on a preview won't be valid on prod (correct).
//   3. Anything else (future custom domain) → REACT_APP_RP_ID env override.
// This lets us test passkey ceremonies on deploy previews without dropping the
// pin entirely — see Phase 6 commit message + docs/auth-phase-1-deployment.md.
const RP_ID = window.location.hostname === 'localhost'
  ? 'localhost'
  : window.location.hostname.endsWith('.netlify.app')
    ? window.location.hostname
    : (process.env.REACT_APP_RP_ID || 'cpx-prototype.netlify.app');
const RP_NAME = 'CPx Admin';

// ── Helpers ──────────────────────────────────────────────────────────────────

async function neonAuth(query, params = {}) {
  const res = await fetch('/api/neon', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ query, params }),
  });
  return res.json();
}

function b64url(buf) {
  const bytes = new Uint8Array(buf);
  let s = '';
  bytes.forEach(b => s += String.fromCharCode(b));
  return btoa(s).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function unb64url(str) {
  const base64 = str.replace(/-/g, '+').replace(/_/g, '/');
  const pad = base64.length % 4 === 0 ? '' : '='.repeat(4 - (base64.length % 4));
  return Uint8Array.from(atob(base64 + pad), c => c.charCodeAt(0));
}

function getDeviceName() {
  const ua = navigator.userAgent;
  if (/iPhone/.test(ua)) return 'iPhone';
  if (/iPad/.test(ua)) return 'iPad';
  if (/Android/.test(ua)) return 'Android';
  if (/Mac/.test(ua)) return 'Mac';
  if (/Windows/.test(ua)) return 'Windows PC';
  return 'Unknown Device';
}

// ── Session management ───────────────────────────────────────────────────────

export function getCurrentUser() {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored ? JSON.parse(stored) : null;
  } catch {
    return null;
  }
}

export function getAccessToken() {
  return getCurrentUser()?.accessToken ?? null;
}

// The sign-in header for a screen that calls a function with its own fetch.
export function sessionHeaders() {
  const token = getAccessToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export function isLoggedIn() {
  return !!getCurrentUser()?.accessToken;
}

export async function validateSession() {
  const token = getAccessToken();
  if (!token) return false;
  const data = await neonAuth('auth_validate_session', { token });
  if (!data?.valid) {
    localStorage.removeItem(STORAGE_KEY);
    return false;
  }
  // Refresh stored user with latest data from server (roles, staffName, etc.)
  const current = getCurrentUser();
  if (current) {
    const updated = { ...current };
    if (data.roles) updated.roles = data.roles;
    if (data.staffName) updated.staffName = data.staffName;
    if (data.id) updated.id = data.id;
    localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
  }
  return true;
}

export async function logout() {
  const token = getAccessToken();
  if (token) {
    neonAuth('auth_logout', { token }).catch(() => {});
  }
  localStorage.removeItem(STORAGE_KEY);
}

// ── Password login (rolled back from passkey-only; will be MFA-gated in PR 1c) ─

export async function login(username, password) {
  const data = await neonAuth('auth_login', { username, password });
  // PR 1c: server can return one of three shapes after password verify —
  //   * { accessToken, ... }                                  — happy path, session minted
  //   * { mfa_required: true, mfa_token }                     — user has MFA, prompt for code
  //   * { mfa_setup_required: true, setup_token }             — user must set up MFA before logging in
  if (data?.mfa_required) return { mfaRequired: true, mfaToken: data.mfa_token };
  if (data?.mfa_setup_required) return { mfaSetupRequired: true, setupToken: data.setup_token };
  if (!data?.accessToken) {
    throw new Error(data?.error || 'Invalid username or password.');
  }
  localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  return data;
}

// PR 1c: complete MFA verification. Called after login() returns { mfaRequired: true }.
export async function verifyMFA(mfaToken, code, rememberDevice) {
  const data = await neonAuth('auth_verify_mfa', { mfaToken, code, rememberDevice: !!rememberDevice });
  if (!data?.accessToken) throw new Error(data?.error || 'MFA verification failed.');
  localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  return data;
}

export async function submitBackupCode(mfaToken, backupCode) {
  const data = await neonAuth('mfa_use_backup_code', { mfaToken, backupCode });
  if (!data?.accessToken) throw new Error(data?.error || 'Backup code did not match.');
  localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  return data;
}

// Mandatory-MFA: forced-enrollment handlers used by Login.js when password verify returned
// mfa_setup_required. These call PUBLIC_QUERIES on the server (no session yet).
export async function setupBeginMfa(setupToken) {
  const data = await neonAuth('mfa_setup_begin', { setupToken });
  if (data?.error) throw new Error(data.error);
  return data; // { secretBase32, otpauthUri, issuer, label }
}

export async function confirmSetupMfa(setupToken, code) {
  const data = await neonAuth('mfa_setup_confirm', { setupToken, code });
  if (!data?.accessToken) throw new Error(data?.error || 'Setup confirmation failed.');
  localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  return data; // { id, staffName, accessToken, roles, backupCodes }
}


// ── Passkey support detection ────────────────────────────────────────────────

export function passkeySupported() {
  return window.PublicKeyCredential !== undefined;
}

// Has a passkey ceremony ever succeeded in THIS browser? Login.js uses it to
// pick the opening view. Offering "Sign in with Windows Hello" to a device that
// holds no credential yields a NotAllowedError the user reads as a broken
// account -- see Matt Cain Jr, 2026-08-20. Per-device, not per-account: it says
// nothing about whether the user has passkeys elsewhere.
const PASSKEY_DEVICE_KEY = 'cpx_passkey_device';

export function deviceHasPasskey() {
  try { return localStorage.getItem(PASSKEY_DEVICE_KEY) === '1'; }
  catch { return false; }
}

export function markPasskeyDevice() {
  try { localStorage.setItem(PASSKEY_DEVICE_KEY, '1'); }
  catch { /* private mode: worst case is one extra click next time */ }
}

export async function hasRegisteredPasskeys() {
  if (!passkeySupported()) return false;
  const data = await neonAuth('auth_has_passkeys');
  return data?.hasPasskeys === true;
}

// ── Passkey registration (new user setup) ────────────────────────────────────

export async function registerPasskey(setupCode) {
  if (!passkeySupported()) throw new Error('Passkeys are not supported on this device.');

  // Step 1: Get challenge and user info from server
  const beginData = await neonAuth('auth_register_begin', { setupCode });
  if (beginData.error) throw new Error(beginData.error);

  const { challenge, user, excludeCredentials } = beginData;

  // Step 2: Create credential on device (triggers Face ID / Touch ID / Windows Hello / PIN)
  const credential = await navigator.credentials.create({
    publicKey: {
      challenge: unb64url(challenge),
      rp: { name: RP_NAME, id: RP_ID },
      user: {
        id: new TextEncoder().encode(user.id),
        name: user.name,
        displayName: user.displayName,
      },
      pubKeyCredParams: [
        { alg: -7, type: 'public-key' },   // ES256 (most devices)
        { alg: -257, type: 'public-key' },  // RS256 (Windows Hello)
      ],
      excludeCredentials: (excludeCredentials || []).map(id => ({
        id: unb64url(id),
        type: 'public-key',
      })),
      authenticatorSelection: {
        authenticatorAttachment: 'platform', // Use device's built-in authenticator (Windows Hello, Face ID, Touch ID)
        residentKey: 'required',            // Discoverable credential (no username needed)
        userVerification: 'required',       // Must verify identity (Face ID, PIN, etc.)
      },
      timeout: 120000,
    },
  });

  // Step 3: Extract public key in SPKI format for server-side verification
  const response = credential.response;
  const publicKeyBytes = new Uint8Array(response.getPublicKey());
  const algorithm = response.getPublicKeyAlgorithm();

  // Step 4: Send credential to server
  const completeData = await neonAuth('auth_register_complete', {
    challenge,
    setupCode,
    credentialId: b64url(credential.rawId),
    publicKey: b64url(publicKeyBytes),
    algorithm,
    transports: response.getTransports?.() || [],
    deviceName: getDeviceName(),
    clientDataJSON: b64url(response.clientDataJSON),
  });

  if (completeData.error) throw new Error(completeData.error);

  // Step 5: Store session
  localStorage.setItem(STORAGE_KEY, JSON.stringify(completeData));
  markPasskeyDevice();
  return completeData;
}

// Register a passkey for the currently logged-in user (no setup code needed)
export async function registerPasskeyForCurrentUser() {
  if (!passkeySupported()) throw new Error('Passkeys are not supported on this device.');
  const token = getAccessToken();
  if (!token) throw new Error('Not logged in.');

  const beginData = await neonAuth('auth_register_begin', { sessionToken: token });
  if (beginData.error) throw new Error(beginData.error);

  const { challenge, user, excludeCredentials } = beginData;

  const credential = await navigator.credentials.create({
    publicKey: {
      challenge: unb64url(challenge),
      rp: { name: RP_NAME, id: RP_ID },
      user: {
        id: new TextEncoder().encode(user.id),
        name: user.name,
        displayName: user.displayName,
      },
      pubKeyCredParams: [
        { alg: -7, type: 'public-key' },
        { alg: -257, type: 'public-key' },
      ],
      excludeCredentials: (excludeCredentials || []).map(id => ({
        id: unb64url(id),
        type: 'public-key',
      })),
      authenticatorSelection: {
        authenticatorAttachment: 'platform',
        residentKey: 'required',
        userVerification: 'required',
      },
      timeout: 120000,
    },
  });

  const response = credential.response;
  const completeData = await neonAuth('auth_register_complete', {
    challenge,
    credentialId: b64url(credential.rawId),
    publicKey: b64url(new Uint8Array(response.getPublicKey())),
    algorithm: response.getPublicKeyAlgorithm(),
    transports: response.getTransports?.() || [],
    deviceName: getDeviceName(),
    clientDataJSON: b64url(response.clientDataJSON),
  });

  if (completeData.error) throw new Error(completeData.error);

  // Update session with new token from registration
  localStorage.setItem(STORAGE_KEY, JSON.stringify(completeData));
  markPasskeyDevice();
  return completeData;
}

// ── Passkey login ────────────────────────────────────────────────────────────

export async function loginWithPasskey() {
  if (!passkeySupported()) throw new Error('Passkeys are not supported on this device.');

  // Step 1: Get challenge from server
  const beginData = await neonAuth('auth_authenticate_begin');
  if (beginData.error) throw new Error(beginData.error);

  const { challenge } = beginData;

  // Step 2: Get assertion from device (triggers Face ID / Touch ID / Windows Hello / PIN)
  // No allowCredentials: we register with residentKey:'required', so these are
  // discoverable passkeys and the platform authenticator finds the right one and
  // shows its own account picker. Naming them explicitly used to be the ONLY way
  // in, which meant any passkey the server left out of that list was unreachable
  // even though it was sitting on the device — see the note in
  // auth_authenticate_begin (netlify/functions/neon.js).
  const assertion = await navigator.credentials.get({
    publicKey: {
      challenge: unb64url(challenge),
      rpId: RP_ID,
      userVerification: 'required',
      timeout: 120000,
    },
  });

  // Step 3: Send assertion to server for verification
  const response = assertion.response;
  const completeData = await neonAuth('auth_authenticate_complete', {
    challenge,
    credentialId: b64url(assertion.rawId),
    authenticatorData: b64url(response.authenticatorData),
    clientDataJSON: b64url(response.clientDataJSON),
    signature: b64url(response.signature),
    userHandle: response.userHandle ? b64url(response.userHandle) : null,
  });

  if (completeData.error) throw new Error(completeData.error);

  // Step 4: Store session
  localStorage.setItem(STORAGE_KEY, JSON.stringify(completeData));
  markPasskeyDevice();
  return completeData;
}
